/**
 * Turning reagent's frames into the four things the UI actually consumes:
 * a run id, message text, board operations and the state snapshot.
 *
 * The shapes here were measured, not assumed — see the header of `sse.ts`.
 */

import type { SseFrame } from './sse';

export type ChatRole = 'human' | 'ai' | 'tool' | 'system';

/** One message as it comes off `messages/*`, already flattened. */
export type WireMessage = {
  id: string;
  type: ChatRole;
  content: string;
  /** `name` on a tool message is the tool that produced it. */
  name: string | null;
  toolCalls: { name: string; id: string | null }[];
  finishReason: string | null;
  modelName: string | null;
  /** Reasoning text, when the model exposes it (shown only under `?debug=1`). */
  reasoning: string | null;
  /**
   * `additional_kwargs.pa_bridge === true` — a `peterhof_duo` bridge line: the
   * small model's own 1–2 phrases, ahead of the turn's real answer, under its
   * OWN message id. This is the restore-path half of knowing it (a checkpoint
   * message carries no `messages/metadata` tags); the live-stream half is the
   * `pa_bridge` tag on `message_meta`, which arrives before the first chunk.
   */
  bridge: boolean;
  /**
   * `additional_kwargs.pa_internal === true` — «the duo speaks with one
   * voice» (2026-09-02): a `peterhof_duo` message from the BIG model, which
   * must never reach the visitor as text — only the small model's bridge and
   * voice do. This is the restore-path half; the live half is the
   * `pa_internal`/`langsmith:hidden` tags on `message_meta`, which chatStore
   * folds into the SAME suppression set this field feeds (either arrival —
   * the tag or this field on a whole message — is enough).
   */
  internal: boolean;
  /**
   * `additional_kwargs.pa_voice === true` — the small model's own FINAL
   * answer under voice mode: an ordinary bubble, styled no differently from
   * any other guide line (only the bridge is quieter). Carried on `Bubble`
   * for future styling; unused visually today.
   */
  voice: boolean;
  /**
   * `additional_kwargs.pa_bridge_kind` — `'progress'` marks a NARRATOR line:
   * `peterhof_orch`'s helper reporting what it just did (a closure it found, a
   * route it rebuilt, a source that went quiet) while the job is still running.
   * A plain `pa_bridge` with no kind is the `say` handoff, which opens the wait.
   * Restore-path half; live, the same fact arrives as the `pa_bridge_progress`
   * run tag on `message_meta`.
   *
   * Optional, like `turnCards` and for the same reason: a message built anywhere
   * else — a test, the worker stream — needs no new field.
   */
  bridgeKind?: string | null;
  /**
   * `additional_kwargs.pa_delegation_id` — which `delegate` call this progress
   * line belongs to, so a job's lines group under its own handoff bubble.
   */
  delegationId?: string | null;
  /**
   * `additional_kwargs.pa_progress_seq` — the order the visitor read the
   * delegation's lines in, and therefore the order a restored thread shows.
   */
  progressSeq?: number | null;
  /**
   * `additional_kwargs.pa_turn_cards` — the cards THIS turn drew, snapshotted
   * by the graph onto its answer (`peterhof_canvas/turn_cards.py`). Present
   * only on `peterhof_orch`'s answers. Optional so a message built anywhere
   * else (a test, the worker stream) needs no new field.
   */
  turnCards?: TurnCard[] | null;
};

/**
 * One card of a turn, as the graph copied it off the board at the end of that
 * turn: `props` is the catalogue node minus `id`/`component`, flat, exactly as
 * it stood on the board — so it validates through the same `validateProps`.
 * A copy, not a reference: a later turn that upserts the same id changes the
 * board and never this.
 */
export type TurnCard = { id: string; component: string; props: Record<string, unknown> };

export type RunMeta = {
  model?: string;
  temperature?: number;
  board_rev?: number;
  turn?: number;
  /** `peterhof_orch`/`peterhof_tandem` only: the model the DELEGATED work
   *  actually ran on, alongside `model` (the orchestrator's own). Absent on
   *  every other graph — `Header.tsx` shows it only when present, exactly
   *  like `peterhof_duo`'s own `voice_model` field the graph doc names. */
  worker_model?: string;
};

export type NormalizedEvent =
  | { kind: 'run_started'; runId: string; attempt: number }
  | { kind: 'message'; message: WireMessage; cumulative: boolean }
  | { kind: 'message_meta'; id: string; tags: string[]; node: string | null }
  | { kind: 'a2ui'; frame: unknown }
  | { kind: 'run_meta'; meta: RunMeta }
  /** `kind: "activity"` — the canvas graph's own progress feed (design doc
   *  §3/§5.6). `payload` is read by `activity/types.ts`'s `readActivityFrame`,
   *  never here: this module only tells the envelope's kinds apart. */
  | { kind: 'activity'; payload: unknown; seq: number }
  /** `kind: "reasoning"` — the per-round summary of the model's own thinking
   *  (reasoning-summary design doc §10.3's contract). `payload` is read by
   *  `activity/reasoningTypes.ts`'s `readReasoningFrame`, never here — same
   *  discipline as `activity` above. */
  | { kind: 'reasoning'; payload: unknown; seq: number }
  /** `kind: "worker_started"` — `peterhof_orch`/`peterhof_tandem` delegated
   *  part of the turn to a worker running on its OWN thread. `runId`/`threadId`
   *  are that worker's own (never the visitor's thread), and `workerStream.ts`
   *  is what opens the second SSE onto them and folds the worker's `a2ui`/
   *  `activity` frames onto this same board/feed. Same compatibility property
   *  as `activity`/`reasoning` above: an old build reads this envelope as
   *  `ui_other` and silently ignores it. */
  | { kind: 'worker_started'; runId: string; threadId: string }
  /** `kind: "turn_cards"` — `peterhof_orch`'s answer carries these cards
   *  (`TurnCard`). The LIVE half: the graph re-emits its answer under the id
   *  the model streamed, which never reaches `messages` again, so the same
   *  snapshot rides this frame naming that bubble. `/state` carries it on the
   *  message itself (`WireMessage.turnCards`). */
  | { kind: 'turn_cards'; messageId: string; cards: TurnCard[] }
  /** `kind: "progress"` — `peterhof_live`'s narrator: the helper (Deep Agents,
   *  running on the worker's own thread) reporting a milestone while the job is
   *  still going. Same shape `peterhof_orch`'s inline narrator writes as
   *  `additional_kwargs` on an ordinary message (`events.ts`'s own `bridgeKind`/
   *  `delegationId`/`progressSeq` fields) — Live sends it as its own `dh_ui`
   *  frame instead, because the helper runs OUTSIDE the main turn's stream
   *  (on the worker's own SIDE thread, `workerStream.ts`), so there is no
   *  in-turn message to hang the tags off. `messageId` is what lets the
   *  eventual report run replay this same line under the same bubble id
   *  (`chatStore.ingest`'s dedupe-by-id) rather than drawing it twice. */
  | { kind: 'progress'; delegationId: string; seq: number; text: string; source: 'model' | 'template'; messageId: string }
  /** A backend-selected retry replaces one specific answer bubble. */
  | { kind: 'answer_replacement'; oldMessageId: string; newMessageId: string }
  /** `kind: "navigation"` — transient walking or arrival route preview for the dev Agent.
   *  It is intentionally not attached to a message or replayed from values. */
  | { kind: 'navigation'; payload: unknown }
  | { kind: 'location_selection'; payload: unknown }
  /** `kind: "worker_done"` — the delegation's job has ended (`peterhof_live`).
   *  `status: "superseded"` never actually reaches the wire this way (a
   *  superseded job's worker never gets to write `worker_done` for it — the
   *  next job's own `worker_started` is what a client sees instead), but the
   *  type carries it anyway, the same defensive completeness `run_end`'s
   *  `status` field has. The frame is the cue for `App.tsx`'s `scheduleReport`:
   *  start the report run once the chat goes idle. */
  | { kind: 'worker_done'; delegationId: string; status: 'done' | 'error' | 'superseded' }
  | { kind: 'ui_other'; uiKind: string; payload: unknown }
  /**
   * `kind: "interrupt"` — the run paused on a `langgraph.interrupt()` call
   * (`peterhof_agent`'s questionnaire tool, today). Arrives as an ordinary
   * `values` frame whose payload carries `__interrupt__`
   * (`[{id, value}, …]`) instead of the graph's state — read here rather than
   * folded into `kind: "values"` because a client with no interrupt handling
   * at all must not hand `__interrupt__` to `applyAttachments` as if it were
   * `state.attachments`. `value`'s shape is the interrupting TOOL's own
   * contract (`store/questionnaireStore.ts`'s `QuestionnaireValue` for
   * `kind: "questionnaire"`) — this module knows only that an interrupt
   * happened, never what it is about; an unrecognised `value.kind` is the
   * consumer's call, not this normalizer's.
   */
  | { kind: 'interrupt'; interrupts: { id: string; value: unknown }[] }
  | { kind: 'values'; values: Record<string, unknown> }
  | { kind: 'run_error'; error: string; message: string }
  | { kind: 'run_end'; status: string }
  | { kind: 'ignored' };

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/**
 * `pa_turn_cards` off a message or a frame, or `null`. An entry without an id
 * or a component is skipped rather than failing the list: one malformed card
 * may not cost the answer its other cards. Whether a component is KNOWN is
 * not decided here — that is the catalogue's question, asked where it draws.
 */
export function readTurnCards(raw: unknown): TurnCard[] | null {
  if (!Array.isArray(raw)) return null;
  const cards: TurnCard[] = [];
  for (const item of raw) {
    if (!isObject(item)) continue;
    const id = str(item.id);
    const component = str(item.component);
    if (!id || !component) continue;
    cards.push({ id, component, props: isObject(item.props) ? item.props : {} });
  }
  return cards.length > 0 ? cards : null;
}

/**
 * `values.__interrupt__` — an id-less entry is dropped rather than failing
 * the whole frame; `value` travels UNPARSED, since what it means is the
 * interrupting tool's contract and not this transport's.
 */
function readInterrupts(raw: unknown): { id: string; value: unknown }[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const items: { id: string; value: unknown }[] = [];
  for (const entry of raw) {
    if (!isObject(entry)) continue;
    const id = str(entry.id);
    if (!id) continue;
    items.push({ id, value: entry.value });
  }
  return items.length > 0 ? items : null;
}

/**
 * `content` is a string on every model we run, but the block form
 * (`[{type:'text',text:…}, {type:'thinking',…}]`) is what a reasoning model
 * emits, and reading it wrong shows the visitor a JSON dump.
 */
function readContent(raw: unknown): { text: string; reasoning: string | null } {
  if (typeof raw === 'string') return { text: raw, reasoning: null };
  if (!Array.isArray(raw)) return { text: '', reasoning: null };
  let text = '';
  let reasoning = '';
  for (const block of raw) {
    if (typeof block === 'string') {
      text += block;
      continue;
    }
    if (!isObject(block)) continue;
    const type = block.type;
    const value = typeof block.text === 'string' ? block.text : '';
    if (type === 'thinking' || type === 'reasoning' || type === 'reasoning_content') {
      reasoning += typeof block.thinking === 'string' ? block.thinking : value;
    } else if (type === 'text' || type === undefined) {
      text += value;
    }
  }
  return { text, reasoning: reasoning || null };
}

export function readMessage(raw: unknown): WireMessage | null {
  if (!isObject(raw)) return null;
  const id = str(raw.id);
  if (!id) return null;
  const { text, reasoning: inline } = readContent(raw.content);
  const extra = isObject(raw.additional_kwargs) ? raw.additional_kwargs : {};
  const meta = isObject(raw.response_metadata) ? raw.response_metadata : {};
  const toolCalls: WireMessage['toolCalls'] = [];
  // A finished call carries `tool_calls`; a streaming one may only have
  // `tool_call_chunks`, and the name lands there first — which is what the
  // «Смотрю афишу музея…» line is built from.
  for (const key of ['tool_calls', 'tool_call_chunks'] as const) {
    const list = raw[key];
    if (!Array.isArray(list)) continue;
    for (const call of list) {
      if (!isObject(call)) continue;
      const name = str(call.name);
      if (!name) continue;
      if (toolCalls.some((seen) => seen.name === name && seen.id === str(call.id))) continue;
      toolCalls.push({ name, id: str(call.id) });
    }
  }
  const type = str(raw.type);
  return {
    id,
    type: type === 'human' || type === 'ai' || type === 'tool' ? type : 'system',
    content: text,
    name: str(raw.name),
    toolCalls,
    finishReason: str(meta.finish_reason),
    modelName: str(meta.model_name),
    reasoning: inline ?? str(extra.reasoning_content),
    bridge: extra.pa_bridge === true,
    internal: extra.pa_internal === true,
    voice: extra.pa_voice === true,
    bridgeKind: str(extra.pa_bridge_kind),
    delegationId: str(extra.pa_delegation_id),
    progressSeq: typeof extra.pa_progress_seq === 'number' ? extra.pa_progress_seq : null,
    turnCards: readTurnCards(extra.pa_turn_cards),
  };
}

/** `messages*` payloads come as `[message]`, `[message, metadata]` or bare. */
function firstMessage(data: unknown): unknown {
  if (Array.isArray(data)) return data[0];
  return data;
}

/**
 * `custom` carries our `dh_ui` envelope. Two wrappings are read because the two
 * readings of reagent disagreed and the graph that emits them is not deployed
 * yet (§12.6) — one extra `??` against a silently blank board.
 */
function readDhUi(data: unknown): Record<string, unknown> | null {
  if (!isObject(data)) return null;
  const direct = data.dh_ui;
  if (isObject(direct)) return direct;
  const chunk = data.chunk;
  if (isObject(chunk) && isObject(chunk.dh_ui)) return chunk.dh_ui;
  // A payload that already IS the envelope (`{kind, v, payload}`) is accepted
  // too: it costs one check and covers an emitter that skips the wrapper.
  if (typeof data.kind === 'string' && 'payload' in data) return data;
  return null;
}

export function normalizeFrame(frame: SseFrame): NormalizedEvent {
  const { event, data } = frame;

  if (event === 'metadata' && isObject(data)) {
    const runId = str(data.run_id);
    if (!runId) return { kind: 'ignored' };
    return {
      kind: 'run_started',
      runId,
      attempt: typeof data.attempt === 'number' ? data.attempt : 1,
    };
  }

  if (event === 'messages' || event === 'messages/partial' || event === 'messages/complete') {
    const message = readMessage(firstMessage(data));
    if (!message) return { kind: 'ignored' };
    // `messages` is the delta shape, the other two are cumulative. Which one a
    // message uses is decided per message id by whichever arrives first (§3.3.1а):
    // mixing them doubles the text in the bubble.
    return { kind: 'message', message, cumulative: event !== 'messages' };
  }

  if (event === 'messages/metadata' && isObject(data)) {
    for (const [id, value] of Object.entries(data)) {
      const meta = isObject(value) && isObject(value.metadata) ? value.metadata : null;
      if (!meta) continue;
      const tags = Array.isArray(meta.tags) ? meta.tags.filter((t): t is string => typeof t === 'string') : [];
      return { kind: 'message_meta', id, tags, node: str(meta.langgraph_node) };
    }
    return { kind: 'ignored' };
  }

  if (event === 'custom') {
    const envelope = readDhUi(data);
    if (!envelope) return { kind: 'ignored' };
    const kind = str(envelope.kind);
    if (kind === 'a2ui') {
      // Handed over WHOLE: `seq`/`rev` are transport metadata that live in the
      // envelope, and the surface store's replay gate reads them. Normalising
      // here would strip exactly what the gate needs.
      return { kind: 'a2ui', frame: data };
    }
    if (kind === 'run_meta' && isObject(envelope.payload)) {
      return { kind: 'run_meta', meta: envelope.payload as RunMeta };
    }
    // Before the general `ui_other` fallback (§5.6) — a pre-canvas frontend
    // would have read this same frame as `ui_other` and silently ignored it,
    // which is the compatibility property this branch preserves for an old
    // build against a new backend.
    if (kind === 'activity' && isObject(envelope.payload)) {
      const seq = typeof envelope.seq === 'number' ? envelope.seq : 0;
      return { kind: 'activity', payload: envelope.payload, seq };
    }
    // Same compatibility property as `activity` above: before this branch
    // existed, a `reasoning` envelope already arrived as `ui_other` and was
    // silently ignored — an old build against a new backend keeps degrading
    // that way rather than throwing.
    if (kind === 'reasoning' && isObject(envelope.payload)) {
      const seq = typeof envelope.seq === 'number' ? envelope.seq : 0;
      return { kind: 'reasoning', payload: envelope.payload, seq };
    }
    // Same compatibility property again: before this branch existed, a
    // `worker_started` envelope already arrived as `ui_other` and was
    // silently ignored — a build without `workerStream.ts` keeps degrading
    // that way against a backend that now sends it.
    if (kind === 'worker_started' && isObject(envelope.payload)) {
      const runId = str(envelope.payload.run_id);
      const threadId = str(envelope.payload.thread_id);
      if (runId && threadId) return { kind: 'worker_started', runId, threadId };
      return { kind: 'ignored' }; // an envelope missing either half names nothing to connect to
    }
    // Same compatibility property again: an older build reads this as
    // `ui_other` and ignores it, and the cards still arrive with `/state`.
    if (kind === 'turn_cards' && isObject(envelope.payload)) {
      const messageId = str(envelope.payload.message_id);
      const cards = readTurnCards(envelope.payload.cards);
      if (messageId && cards) return { kind: 'turn_cards', messageId, cards };
      return { kind: 'ignored' };
    }
    // Same compatibility property as every kind above: a build without
    // `peterhof_live`'s handling reads this as `ui_other` and ignores it.
    if (kind === 'progress' && isObject(envelope.payload)) {
      const delegationId = str(envelope.payload.delegation_id);
      const messageId = str(envelope.payload.message_id);
      const text = str(envelope.payload.text);
      const seq = typeof envelope.payload.seq === 'number' ? envelope.payload.seq : null;
      const source = envelope.payload.source;
      if (
        delegationId &&
        messageId &&
        text &&
        seq !== null &&
        (source === 'model' || source === 'template')
      ) {
        return { kind: 'progress', delegationId, seq, text, source, messageId };
      }
      return { kind: 'ignored' };
    }
    if (kind === 'answer_replacement' && envelope.v === 1 && isObject(envelope.payload)) {
      const oldMessageId = str(envelope.payload.old_message_id);
      const newMessageId = str(envelope.payload.new_message_id);
      if (oldMessageId && newMessageId && oldMessageId !== newMessageId) {
        return { kind: 'answer_replacement', oldMessageId, newMessageId };
      }
      return { kind: 'ignored' };
    }
    if ((kind === 'navigation' || kind === 'location_selection') && envelope.v === 1 && isObject(envelope.payload)) {
      return { kind, payload: envelope.payload };
    }
    if (kind === 'worker_done' && isObject(envelope.payload)) {
      const delegationId = str(envelope.payload.delegation_id);
      const status = envelope.payload.status;
      if (delegationId && (status === 'done' || status === 'error' || status === 'superseded')) {
        return { kind: 'worker_done', delegationId, status };
      }
      return { kind: 'ignored' };
    }
    return { kind: 'ui_other', uiKind: kind ?? 'unknown', payload: envelope.payload };
  }

  if (event === 'values') {
    // Measured flat (§12.3); the `chunk` read costs nothing and covers the
    // other reading of reagent should this instance ever be rebuilt from it.
    const values = isObject(data) ? (isObject(data.chunk) ? data.chunk : data) : null;
    if (!values) return { kind: 'ignored' };
    const interrupts = readInterrupts(values.__interrupt__);
    if (interrupts) return { kind: 'interrupt', interrupts };
    return { kind: 'values', values };
  }

  if (event === 'error' && isObject(data)) {
    return {
      kind: 'run_error',
      error: str(data.error) ?? 'error',
      message: str(data.message) ?? '',
    };
  }

  if (event === 'end') {
    const status = isObject(data) ? (str(data.status) ?? 'success') : 'success';
    return { kind: 'run_end', status };
  }

  return { kind: 'ignored' };
}
